/**
 * PF Availability — standalone live-availability display.
 *
 * Supplies the same live availability the variant picker does (estimated ship
 * time + ATC purchasability) on pages that DON'T render <pf-variant-picker>:
 * legacy-engine products, single-variant products, featured-product, and custom
 * templates. Fetches /availability by SKU, indexes the variant family, and
 * re-picks on the theme's on:variant:change event.
 *
 * Stands down when a <pf-variant-picker> owns the same section — no double fetch.
 *
 * Attributes (set by pf-availability.liquid):
 *   data-sku                   Current variant SKU.
 *   data-availability-endpoint URL template containing a "{sku}" placeholder.
 */

import {
  availabilityUrlFor,
  fetchAvailability,
} from './pf-variant-picker/engine/availability-fetch.js';
import {
  updateShipTime,
  updateAvailabilityLabel,
  updateAddToCartButton,
} from './pf-variant-picker/engine/availability-view.js';

if (!customElements.get('pf-availability')) {
  class PfAvailability extends HTMLElement {
    connectedCallback() {
      this._section =
        this.closest('.js-quickbuy') ||
        this.closest('.cc-featured-product') ||
        this.closest('.shopify-section') ||
        document;

      // The picker already owns availability wherever it renders.
      if (this._section.querySelector('pf-variant-picker')) return;

      this._sku = this.dataset.sku || '';
      this._bySku = new Map();

      this._boundOnChange = this._onVariantChange.bind(this);
      document.addEventListener('on:variant:change', this._boundOnChange);

      this._fetch(this._sku);
    }

    disconnectedCallback() {
      this._abort?.abort();
      if (this._boundOnChange) {
        document.removeEventListener('on:variant:change', this._boundOnChange);
      }
    }

    _onVariantChange(evt) {
      const sku = evt.detail?.variant?.sku;
      if (!sku) return;
      this._sku = sku;
      if (this._bySku.has(sku)) {
        this._render(this._bySku.get(sku));
      } else {
        // A variant outside the fetched family — refetch for it.
        this._fetch(sku);
      }
    }

    _fetch(sku) {
      const url = availabilityUrlFor(this.dataset.availabilityEndpoint, sku);
      if (!url) return;
      this._abort?.abort();
      const controller = new AbortController();
      this._abort = controller;
      fetchAvailability(url, controller.signal).then(
        (variants) => {
          if (this._abort !== controller || !this.isConnected) return;
          this._index(variants);
          this._render(this._bySku.get(this._sku) ?? null);
        },
        (err) => {
          if (controller.signal.aborted || this._abort !== controller) return;
          // Degrade quietly — Shopify's SSR price/ATC stays functional.
          console.warn('[pf-availability] fetch failed:', err?.message);
        }
      );
    }

    _index(variants) {
      for (const v of Array.isArray(variants) ? variants : []) {
        if (v?.sku) this._bySku.set(v.sku, v);
      }
    }

    // Ship time always reflects the current pick (hidden when none). The ATC is
    // only touched when we have live data for the selected SKU — otherwise the
    // server-rendered button is left exactly as Shopify set it.
    _render(entry) {
      const variant = entry
        ? {
            id: entry.sku,
            sku: entry.sku,
            available: entry.available ?? null,
            estimatedShipTime: entry.estimatedShipTime ?? null,
          }
        : null;
      updateShipTime(this._section, variant);
      updateAvailabilityLabel(this._section, variant);
      if (!variant) return;
      updateAddToCartButton(this._section.querySelector('.js-product-form'), {
        productAvailable: this.classList.contains(
          'pf-availability--product-available'
        ),
        variant,
      });
    }
  }

  customElements.define('pf-availability', PfAvailability);
}
